import React, { useState } from 'react';
import { X, Key, AlertTriangle, Mail, User } from 'lucide-react';
import { resetUserPassword } from '../lib/admin';
import type { UserStats } from '../types/admin';

interface Props {
  user: UserStats;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ResetUserPasswordModal({ user, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);

  const expectedConfirmText = 'RESET PASSWORD';
  const isConfirmValid = confirmText === expectedConfirmText;

  const handleReset = async () => {
    if (!isConfirmValid) {
      setError('Please type RESET PASSWORD to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const result = await resetUserPassword(user.user_id, reason);
      setGeneratedPassword(result.newPassword);
      setSuccess(true);
      
      // Auto close after showing success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 10000); // Give more time to copy password
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
        <div className="card-pastel p-6 max-w-md w-full">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="bg-yellow-50 p-4 rounded-full">
                <Key className="w-12 h-12 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 font-poppins">Password Reset Complete!</h3>
            <p className="text-gray-600 font-poppins">
              New password generated for {user.email}
            </p>
            
            {/* Security Warning */}
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="text-red-700 text-sm font-medium mb-2 font-poppins">
                ⚠️ SECURITY WARNING
              </p>
              <p className="text-red-600 text-xs font-poppins">
                This password will only be shown once. Copy it securely and share it with the user through a secure channel. The user should change this password immediately after logging in.
              </p>
            </div>

            {/* Generated Password */}
            <div className="bg-white/60 p-4 rounded-lg">
              <p className="text-gray-600 text-sm mb-2 font-poppins">New Password:</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-white/80 text-gray-800 font-mono text-lg rounded border select-all">
                  {generatedPassword}
                </code>
                <button
                  onClick={copyPassword}
                  className={`px-3 py-2 rounded transition-colors font-poppins ${
                    passwordCopied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-pastel-blue-600 text-white hover:bg-pastel-blue-700'
                  }`}
                >
                  {passwordCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="bg-pastel-blue-50 p-4 rounded-lg">
              <p className="text-pastel-blue-700 text-sm font-poppins">
                🔐 Share this password with the user securely and instruct them to change it immediately after logging in.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="card-pastel p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Key className="w-6 h-6 text-pastel-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800 font-poppins">Reset User Password</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Warning */}
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-700 mb-2 font-poppins">⚠️ SECURITY WARNING: Direct Password Reset</h4>
                <p className="text-red-600 text-sm mb-2 font-poppins">
                  This action will:
                </p>
                <ul className="text-red-600 text-sm space-y-1 list-disc list-inside font-poppins">
                  <li><strong>Generate a new password and show it to you</strong></li>
                  <li><strong>Immediately replace their current password</strong></li>
                  <li><strong>Give you access to their account credentials</strong></li>
                  <li>Log this action in the admin audit trail</li>
                </ul>
                <p className="text-red-700 text-sm mt-3 font-medium font-poppins">
                  🚨 You will be responsible for securely sharing this password with the user and ensuring they change it immediately.
                </p>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="bg-white/60 p-4 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <User className="w-5 h-5 text-gray-800" />
              <h4 className="font-medium text-gray-800 font-poppins">User to Reset</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-poppins">
              <div>
                <p><span className="text-gray-500">Email:</span></p>
                <p className="text-gray-800 font-medium">{user.email}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Name:</span></p>
                <p className="text-gray-800 font-medium">{user.full_name || 'Not provided'}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Username:</span></p>
                <p className="text-gray-800 font-medium">{user.username || 'Not set'}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Member Since:</span></p>
                <p className="text-gray-800 font-medium">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2 font-poppins">
              Reason for Password Reset (Required for audit trail)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-pastel-blue-500 focus:border-pastel-blue-500 font-poppins"
              rows={3}
              placeholder="Enter reason for password reset (e.g., user forgot password, security concern, user request, etc.)"
              required
            />
          </div>

          {/* Confirmation */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2 font-poppins">
              Type <span className="font-bold text-pastel-blue-600 bg-pastel-blue-50 px-2 py-1 rounded">RESET PASSWORD</span> to confirm this action
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-pastel-blue-500 focus:border-pastel-blue-500 font-poppins"
              placeholder="RESET PASSWORD"
            />
          </div>

          {/* Final Notice */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-yellow-700 text-sm text-center font-medium font-poppins">
              🔐 A new password will be generated and shown to you. Share it securely with the user.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-white/40">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 border border-white/40 rounded-md hover:bg-white/60 transition-colors font-poppins"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              disabled={loading || !isConfirmValid || !reason.trim()}
              className="flex items-center justify-center space-x-2 px-6 py-2 bg-pastel-blue-600 text-white rounded-md hover:bg-pastel-blue-700 disabled:opacity-50 transition-colors font-poppins"
            >
              <Key className="w-4 h-4" />
              <span>{loading ? 'Sending Reset Email...' : 'Reset Password'}</span>
              <span>{loading ? 'Generating New Password...' : 'Generate New Password'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}